import React, { useEffect, useState } from "react";
import ImageBox from "./ImageBox"; // Import the ImageBox component
import "./Setting.css";
import { FaEye, FaEyeSlash, FaFont, FaBolt, FaImage, FaEdit, FaCode } from 'react-icons/fa';
import FormSetting from './FormSetting';
import TabSettings from './TabSettings';
import SliderSettings from './SliderSettings';
import CartSettings from './CartSettings';  // Add this import
import RichTextEditor from '../RichTextEditor/RichTextEditor'; // Import RichTextEditor
import EmbedCodeEditor from '../EmbedCodeEditor/EmbedCodeEditor'; // Import EmbedCodeEditor
import NavbarSettings from './NavbarSettings'; // Add this import

const findParentOfType = (elements, elementId, targetType) => {
  let current = elements.find(el => el.id === elementId);
  while (current) {
    if (current.type === targetType) {
      return current;
    }
    current = elements.find(el => el.id === current.parentId);
  }
  return null;
};

const Setting = ({
  selectedElement,
  updateElementText,
  updateElementClassName,
  setSelectedElement,
  assets,
  setShowAssetsSidebar,
  selectedAssetFile,
  onSelectAsset, // Receive the asset selection handler
  pages, // Add pages prop
  setPages, // Add this prop
  elements, // Make sure elements is passed as prop
  setElements, // Add this prop
  currentPageId, // Add this prop
  selectedContentItem, // Add this prop
  cmsContentTypes,    // Add these new props
  cmsContent,         // Add these new props
  classStylesRegistry,
  setClassStylesRegistry,
  selectedDevice,
  showRichTextEditorForNewElement // New prop to auto-show rich text editor
}) => {
  const [selectedImage, setSelectedImage] = useState(null); // State to hold the selected image
  const [text, setText] = useState(selectedElement.text || "");
  const [selectedPage, setSelectedPage] = useState("");
  const [selectedField, setSelectedField] = useState("");
  const [selectedImageField, setSelectedImageField] = useState("static"); // Add state for image field selection
  const [displayType, setDisplayType] = useState(selectedElement.displayType || "list");
  const [limit, setLimit] = useState(selectedElement.limit || 10);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [className, setClassName] = useState(selectedElement.className || "");
  const [contentTypeId, setContentTypeId] = useState(selectedElement.contentTypeId || "");
  const [showRichTextEditor, setShowRichTextEditor] = useState(false);
  
  // Map settings
  const [mapLocation, setMapLocation] = useState(selectedElement.mapLocation || 'New York, NY');
  const [mapZoom, setMapZoom] = useState(selectedElement.mapZoom || '13');

  // Video settings
  const [videoSrc, setVideoSrc] = useState(selectedElement.videoSrc || '');
  const [videoType, setVideoType] = useState(selectedElement.videoType || 'youtube');
  const [autoplay, setAutoplay] = useState(selectedElement.autoplay || false);
  const [controls, setControls] = useState(selectedElement.controls !== false);
  const [loop, setLoop] = useState(selectedElement.loop || false);
  const [mute, setMute] = useState(selectedElement.mute || false);

  // Embed code settings
  const [embedCode, setEmbedCode] = useState(selectedElement.embedCode || '');
  const [showEmbedCodeEditor, setShowEmbedCodeEditor] = useState(false);
  
  // Dropdown settings
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [newOptionValue, setNewOptionValue] = useState('');

  // Find if element is under a CMS element
  const parentCmsElement = findParentOfType(elements, selectedElement?.id, 'cms-content');

  // Get current page and check if it's a template
  const currentPage = pages.find(p => p.id === currentPageId);
  const isTemplatePage = currentPage?.isTemplate;

  // Get the content type for the parent CMS element if it exists
  const parentContentType = parentCmsElement 
    ? cmsContentTypes?.find(type => type._id === parentCmsElement.contentTypeId)
    : null;


  useEffect(() => {    
    if (!selectedContentItem || !elements || elements.length === 0) return;
    
    // Track elements that need updating
    const elementsToUpdate = [];
    
    // Find all elements with dynamic fields that need updating
    elements.forEach(element => {
      let needsUpdate = false;
      let updatedElement = { ...element };
      
      // Handle dynamic text fields
      if (element.dynamicField && selectedContentItem?.data) {
        const fieldValue = selectedContentItem.data[element.dynamicField];
        // Update even if fieldValue is undefined or empty, to ensure field binding is maintained
        updatedElement.text = fieldValue !== undefined ? fieldValue : '';
        needsUpdate = true;
      }
      
      // Handle dynamic image fields
      if (element.type === "image" && element.dynamicImageField && selectedContentItem?.data) {
        const imageUrl = selectedContentItem.data[element.dynamicImageField];
        
        // Update the image URL if available
        if (imageUrl) {
          updatedElement.styles = {
            ...updatedElement.styles,
            backgroundImage: `url(${imageUrl})`
          };
        } else {
          // If no image URL is available, set a placeholder
          updatedElement.styles = {
            ...updatedElement.styles,
            backgroundImage: `url(https://placehold.co/150x150)`
          };
        }
        needsUpdate = true;
      }
      
      // If this element needs updating, add it to our list
      if (needsUpdate) {
        elementsToUpdate.push(updatedElement);
      }
    });
    
    // Only proceed if we have elements to update
    if (elementsToUpdate.length > 0) {
      // First, update each element individually using updateElementText
      elementsToUpdate.forEach(element => {
        updateElementText(element.id, element);
      });
      
      // Then, update the elements array once with all changes
      const updatedElements = elements.map(element => {
        const updatedElement = elementsToUpdate.find(e => e.id === element.id);
        return updatedElement || element;
      });
      
      // Update the elements state
      setElements(updatedElements);
      
      // Update selected element if it was one of the changed ones
      if (selectedElement) {
        const updatedSelectedElement = elementsToUpdate.find(el => el.id === selectedElement.id);
        if (updatedSelectedElement) {
          setSelectedElement(updatedSelectedElement);
          
          // Update image preview if the selected element is an image with dynamic field
          if (updatedSelectedElement.type === "image" && updatedSelectedElement.dynamicImageField) {
            const imageUrl = selectedContentItem.data[updatedSelectedElement.dynamicImageField];
            if (imageUrl) {
              setSelectedImage({
                url: imageUrl,
                name: `Dynamic: ${updatedSelectedElement.dynamicImageField}`,
                dimensions: {
                  width: updatedSelectedElement.styles.width
                    ? parseInt(updatedSelectedElement.styles.width)
                    : 150,
                  height: "auto",
                },
              });
            } else {
              // Show placeholder for empty image
              setSelectedImage({
                url: "https://placehold.co/150x150",
                name: `Dynamic: ${updatedSelectedElement.dynamicImageField} (empty)`,
                dimensions: {
                  width: updatedSelectedElement.styles.width
                    ? parseInt(updatedSelectedElement.styles.width)
                    : 150,
                  height: "auto",
                },
              });
            }
          }
        }
      }
    }
  // Only depend on selectedContentItem changes, not elements
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContentItem]);

  useEffect(() => {
    if (selectedElement) {
      if (selectedElement.dynamicField) {
        // For dynamic fields, update both text and selected field
        setSelectedField(selectedElement.dynamicField);
        const fieldValue = selectedContentItem?.data[selectedElement.dynamicField];
        setText(fieldValue || '');
      } else {
        // For static text or no field selected
        setSelectedField('static');
        setText(selectedElement.text || '');
      }
      
      // Set image field selection state
      if (selectedElement.dynamicImageField) {
        setSelectedImageField(selectedElement.dynamicImageField);
      } else {
        setSelectedImageField('static');
      }
      
      setSelectedPage(selectedElement.linkTo || '');
      setClassName(selectedElement.className || "");
      setContentTypeId(selectedElement.contentTypeId || "");
      setDisplayType(selectedElement.displayType || "list");
      setLimit(selectedElement.limit || 10);
    }
  }, [selectedElement, selectedContentItem]);

  useEffect(() => {
    // console.log(selectedAssetFile);
    if (selectedAssetFile) {
      handleAssetSelect(selectedAssetFile);
    }
  }, [selectedAssetFile]);

  // Update selectedImage whenever selectedElement changes
  useEffect(() => {
    if (selectedElement && selectedElement.type === "image") {
      // Check if this element has a dynamic image field
      if (selectedElement.dynamicImageField && selectedContentItem?.data) {
        // Get the image URL from the selected content item
        const imageUrl = selectedContentItem.data[selectedElement.dynamicImageField];
        
        if (imageUrl) {
          // Set the image preview to the dynamic image from the current content item
          setSelectedImage({
            url: imageUrl,
            name: `Dynamic: ${selectedElement.dynamicImageField}`,
            dimensions: {
              width: selectedElement.styles.width
                ? parseInt(selectedElement.styles.width)
                : 150,
              height: "auto",
            },
          });
        } else {
          // Show placeholder for empty image field
          setSelectedImage({
            url: "https://placehold.co/150x150",
            name: `Dynamic: ${selectedElement.dynamicImageField} (empty)`,
            dimensions: {
              width: selectedElement.styles.width
                ? parseInt(selectedElement.styles.width)
                : 150,
              height: "auto",
            },
          });
        }
        
        // Also update the selectedImageField state to match
        setSelectedImageField(selectedElement.dynamicImageField);
      } 
      // For static images, use the background image from styles
      else {
        const backgroundImage = selectedElement.styles.backgroundImage
          ? selectedElement.styles.backgroundImage
              .replace("url(", "")
              .replace(")", "")
              .replace(/"/g, "")
          : "https://placehold.co/150x150";

        // Check if the URL is the placeholder
        const name =
          backgroundImage === "https://placehold.co/150x150"
            ? "New Image" // Use "New Image" if the URL is the placeholder
            : backgroundImage.split("/").pop().split("-").pop(); // Extract the name from the URL

        setSelectedImage({
          url: backgroundImage,
          name: name, // Use the extracted name or "New Image"
          dimensions: {
            width: selectedElement.styles.width
              ? parseInt(selectedElement.styles.width)
              : 150,
            height: "auto", // You can set this dynamically if needed
          },
        });
        
        // Reset the image field selection to static
        setSelectedImageField("static");
      }
    }
  }, [selectedElement, selectedContentItem]);

  // Add this console.log to help debug
  useEffect(() => {
  }, [parentCmsElement]);

  // Auto-open the rich text editor when a new rich-text element is added
  useEffect(() => {
    if (showRichTextEditorForNewElement && selectedElement.type === 'rich-text') {
      setShowRichTextEditor(true);
    }
  }, [showRichTextEditorForNewElement, selectedElement]);

  // Parse dropdown options when element is a dropdown
  useEffect(() => {
    if (selectedElement.type === 'dropdown') {
      // Parse options from child elements
      const options = [];
      
      if (selectedElement.children && selectedElement.children.length > 0) {
        selectedElement.children.forEach(childId => {
          const optionElement = elements.find(el => el.id === childId && el.type === 'option');
          if (optionElement) {
            options.push({
              label: optionElement.text || '',
              value: optionElement.value || '',
              id: optionElement.id // Store the ID to make updates easier
            });
          }
        });
      }
      
      setDropdownOptions(options);
    }
  }, [selectedElement, elements]);

  if (!selectedElement) return null;

  // Update isTextEditable to exclude rich-text type
  const isTextEditable = [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', // Headings
    'p', 'button', 'link', 'text', 'link-block' // Content elements
  ].includes(selectedElement.type);

  const isLinkable = ['button', 'link', 'link-block'].includes(selectedElement.type);

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    // Update the element with the new text while preserving other properties
    updateElementText(selectedElement.id, {
      ...selectedElement,
      text: newText,
      // Clear dynamic field if user is manually entering text
      dynamicField: undefined
    });
  };

  const handlePageSelect = (e) => {
    console.log("Target: " + e.target.value);
    setSelectedPage(e.target.value);
    updateElementText(selectedElement.id, {
      ...selectedElement,
      linkTo: e.target.value
    });
  };

  const handleAssetSelect = (asset) => {
    if (selectedElement.type === "image") {
      // console.log("Selected Asset:", asset); // Log the selected asset
      const updatedElement = {
        ...selectedElement,
        src: asset.url,
        styles: {
          ...selectedElement.styles,
          backgroundImage: `url(${asset.url})`, // Set the background image to the selected asset URL
          width: `${asset.dimensions.width}px`, // Set width from asset dimensions
          height: "auto", // Set height to auto
        },
      };
      // console.log("Updated Element:", updatedElement); // Log the updated element
      updateElementText(selectedElement.id, updatedElement); // Update the element
      setSelectedImage({
        url: asset.url, // Use the original asset URL
        name: asset.fileName, // Use the asset file name
        dimensions: asset.dimensions,
      }); // Set the selected image
      setShowAssetsSidebar(false); // Close the asset picker
      onSelectAsset(null);
    }
  };

  // Update the field selection handler
  const handleFieldSelect = (field) => {
    setSelectedField(field);
    if (field === 'static') {
      return;
    }

    // Get the current value of the selected field
    const fieldValue = selectedContentItem?.data[field];
    
    // Find field definition to check if it's rich text
    const fieldDef = isTemplatePage
      ? selectedContentItem?.contentType?.fields?.find(f => f.name === field)
      : parentContentType?.fields?.find(f => f.name === field);
    
    const isRichText = fieldDef?.type === 'richText';
    
    // Update element with both the dynamic field and current value
    updateElementText(selectedElement.id, {
      ...selectedElement,
      text: fieldValue || '', // Use the actual field value
      dynamicField: field,
      isRichTextContent: isRichText // Add flag for rich text content
    });

    // Update the text state to show the current value
    setText(fieldValue || '');
  };

  // Update the handleVisibilityChange function
  const handleVisibilityChange = (visibility) => {
    const updatedElement = {
      ...selectedElement,
      visibility: visibility, // Update visibility property
      styles: {
        ...selectedElement.styles
      }
    };

    // Only add visibility style if hidden
    if (visibility === 'hidden') {
      updatedElement.styles.visibility = 'hidden';
    } else {
      // Remove visibility style if visible
      const { visibility: _, ...restStyles } = updatedElement.styles;
      updatedElement.styles = restStyles;
    }
    
    // Update both states
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleClassNameChange = (e) => {
    const newClassName = e.target.value;
    setClassName(newClassName);
    updateElementClassName(selectedElement.id, newClassName);
  };

  const handleContentTypeChange = (e) => {
    const newContentTypeId = e.target.value;
    setContentTypeId(newContentTypeId);
    
    // Update the element with new content type
    const updatedElement = {
      ...selectedElement,
      contentTypeId: newContentTypeId,
      displayType: displayType,
      limit: limit,
      sortBy: sortBy,
      sortOrder: sortOrder
    };
    
    // Update both the selected element and the element in the elements array
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleDisplayTypeChange = (e) => {
    const newDisplayType = e.target.value;
    setDisplayType(newDisplayType);
    
    // Update the element with new display type
    const updatedElement = {
      ...selectedElement,
      displayType: newDisplayType,
      contentTypeId: contentTypeId,
      limit: limit,
      sortBy: sortBy,
      sortOrder: sortOrder
    };
    
    // Update both the selected element and the element in the elements array
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value) || 10;
    setLimit(newLimit);
    
    // Update the element with new limit
    const updatedElement = {
      ...selectedElement,
      limit: newLimit,
      contentTypeId: contentTypeId,
      displayType: displayType,
      sortBy: sortBy,
      sortOrder: sortOrder
    };
    
    // Update both the selected element and the element in the elements array
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleSortByChange = (e) => {
    const newSortBy = e.target.value;
    setSortBy(newSortBy);
    
    // Update the element with new sort by
    const updatedElement = {
      ...selectedElement,
      sortBy: newSortBy,
      contentTypeId: contentTypeId,
      displayType: displayType,
      limit: limit,
      sortOrder: sortOrder
    };
    
    // Update both the selected element and the element in the elements array
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleSortOrderChange = (e) => {
    const newSortOrder = e.target.value;
    setSortOrder(newSortOrder);
    
    // Update the element with new sort order
    const updatedElement = {
      ...selectedElement,
      sortOrder: newSortOrder,
      contentTypeId: contentTypeId,
      displayType: displayType,
      limit: limit,
      sortBy: sortBy
    };
    
    // Update both the selected element and the element in the elements array
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const isFormElement = ['form', 'form-block', 'label', 'input'].includes(selectedElement.type);

  // Add this function to handle dynamic image field selection
  const handleImageFieldSelect = (field) => {
    setSelectedImageField(field);
    
    if (field === 'static') {
      // If static is selected, keep the current image
      return;
    }
    
    // Get the image URL from the selected content item's field
    const imageUrl = selectedContentItem?.data[field];
    console.log('Selected field:', field, 'Image URL:', imageUrl);
    
    if (imageUrl) {
      // Update the element with the dynamic image field
      const updatedElement = {
        ...selectedElement,
        dynamicImageField: field,
        styles: {
          ...selectedElement.styles,
          backgroundImage: `url(${imageUrl})`,
        }
      };
      
      // Update the element
      updateElementText(selectedElement.id, updatedElement);
      
      // Update the selected image preview
      setSelectedImage({
        url: imageUrl,
        name: `Dynamic: ${field}`,
        dimensions: {
          width: selectedElement.styles.width
            ? parseInt(selectedElement.styles.width)
            : 150,
          height: "auto",
        },
      });
    } else {
      // Handle case when the field doesn't contain a valid image URL
      const updatedElement = {
        ...selectedElement,
        dynamicImageField: field,
      };
      
      // Update the element to store the dynamic field reference even if current value is empty
      updateElementText(selectedElement.id, updatedElement);
      
      // Show placeholder for empty image
      setSelectedImage({
        url: "https://placehold.co/150x150",
        name: `Dynamic: ${field} (empty)`,
        dimensions: {
          width: selectedElement.styles.width
            ? parseInt(selectedElement.styles.width)
            : 150,
          height: "auto",
        },
      });
    }
  };

  // Add a handler for rich text editor changes
  const handleRichTextChange = (htmlContent) => {
    setText(htmlContent);
    updateElementText(selectedElement.id, {
      ...selectedElement,
      text: htmlContent,
      dynamicField: undefined
    });
  };

  // Add map location and zoom handlers
  const handleMapLocationChange = (e) => {
    const newLocation = e.target.value;
    setMapLocation(newLocation);
    
    // Update the element with new map location
    const updatedElement = {
      ...selectedElement,
      mapLocation: newLocation
    };
    
    // Update both the selected element and the element in the elements array
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleMapZoomChange = (e) => {
    const newZoom = e.target.value;
    setMapZoom(newZoom);
    
    // Update the element with new map zoom
    const updatedElement = {
      ...selectedElement,
      mapZoom: newZoom
    };
    
    // Update both the selected element and the element in the elements array
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  // Video settings handlers
  const handleVideoSrcChange = (e) => {
    const newSrc = e.target.value;
    setVideoSrc(newSrc);
    
    // Update the element with new video source
    const updatedElement = {
      ...selectedElement,
      videoSrc: newSrc
    };
    
    // Update both the selected element and the element in the elements array
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleVideoTypeChange = (e) => {
    const newType = e.target.value;
    setVideoType(newType);
    
    // Update the element with new video type
    const updatedElement = {
      ...selectedElement,
      videoType: newType
    };
    
    // Update both the selected element and the element in the elements array
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleToggleAutoplay = (e) => {
    const newValue = e.target.checked;
    setAutoplay(newValue);
    
    // Update the element
    const updatedElement = {
      ...selectedElement,
      autoplay: newValue
    };
    
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleToggleControls = (e) => {
    const newValue = e.target.checked;
    setControls(newValue);
    
    // Update the element
    const updatedElement = {
      ...selectedElement,
      controls: newValue
    };
    
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleToggleLoop = (e) => {
    const newValue = e.target.checked;
    setLoop(newValue);
    
    // Update the element
    const updatedElement = {
      ...selectedElement,
      loop: newValue
    };
    
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleToggleMute = (e) => {
    const newValue = e.target.checked;
    setMute(newValue);
    
    // Update the element
    const updatedElement = {
      ...selectedElement,
      mute: newValue
    };
    
    setSelectedElement(updatedElement);
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleEmbedCodeChange = (e) => {
    const newEmbedCode = e.target.value;
    setEmbedCode(newEmbedCode);
    
    const updatedElement = {
      ...selectedElement,
      embedCode: newEmbedCode
    };
    
    updateElementText(selectedElement.id, updatedElement);
  };

  const handleOpenEmbedCodeEditor = () => {
    setShowEmbedCodeEditor(true);
  };

  const handleCloseEmbedCodeEditor = () => {
    setShowEmbedCodeEditor(false);
  };

  const handleSaveEmbedCode = (newCode) => {
    setEmbedCode(newCode);
    
    const updatedElement = {
      ...selectedElement,
      embedCode: newCode
    };
    
    updateElementText(selectedElement.id, updatedElement);
  };

  // Add a function to add dropdown options
  const handleAddDropdownOption = () => {
    if (!newOptionLabel.trim()) return;
    
    const newOption = {
      label: newOptionLabel.trim(),
      value: newOptionValue.trim() || newOptionLabel.trim().toLowerCase().replace(/\s+/g, '-')
    };
    
    // Reset input fields
    setNewOptionLabel('');
    setNewOptionValue('');
    
    // Add new option as a child element
    const newOptionId = Date.now();
    const newOptionElement = {
      id: newOptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      hierarchyName: `Option ${dropdownOptions.length + 1}`,
      text: newOption.label,
      value: newOption.value,
      parentId: selectedElement.id,
      visibility: 'visible'
    };
    
    // Update the dropdown element's children array
    const updatedDropdown = {
      ...selectedElement,
      children: [...(selectedElement.children || []), newOptionId]
    };
    
    // Update the elements array with the new option element
    const updatedElements = [...elements, newOptionElement];
    setElements(updatedElements);
    
    // Update dropdown options state immediately
    const updatedOptions = [...dropdownOptions, {...newOption, id: newOptionId}];
    setDropdownOptions(updatedOptions);
    
    // Update the selected element in both local and parent state
    setSelectedElement(updatedDropdown);
    updateElementText(selectedElement.id, updatedDropdown);
  };
  
  // Add a function to remove dropdown options
  const handleRemoveDropdownOption = (index) => {
    // Find the child element ID to remove
    const childId = selectedElement.children?.[index];
    if (!childId) return;
    
    // Create updated options array without the removed option
    const updatedOptions = dropdownOptions.filter((_, i) => i !== index);
    setDropdownOptions(updatedOptions);
    
    // Create updated children array without the removed child
    const updatedChildren = (selectedElement.children || []).filter(id => id !== childId);
    
    // Update the dropdown element's children
    const updatedDropdown = {
      ...selectedElement,
      children: updatedChildren
    };
    
    // Update the elements array (filter out the removed option)
    const updatedElements = elements.filter(el => el.id !== childId);
    setElements(updatedElements);
    
    // Update the selected element in both local and parent state
    setSelectedElement(updatedDropdown);
    updateElementText(selectedElement.id, updatedDropdown);
  };
  
  // Add functions to move options up and down
  const handleMoveOptionUp = (index) => {
    if (index === 0) return; // Can't move up if already at the top
    
    if (selectedElement.children && selectedElement.children.length > index) {
      // Create a copy of the children array
      const updatedChildren = [...selectedElement.children];
      
      // Swap the children
      const tempChild = updatedChildren[index];
      updatedChildren[index] = updatedChildren[index - 1];
      updatedChildren[index - 1] = tempChild;
      
      // Create updated dropdown with new children order
      const updatedDropdown = {
        ...selectedElement,
        children: updatedChildren
      };
      
      // Also update the dropdownOptions array to match
      const updatedOptions = [...dropdownOptions];
      const tempOption = updatedOptions[index];
      updatedOptions[index] = updatedOptions[index - 1];
      updatedOptions[index - 1] = tempOption;
      setDropdownOptions(updatedOptions);
      
      // Update the selected element in both local and parent state
      setSelectedElement(updatedDropdown);
      updateElementText(selectedElement.id, updatedDropdown);
    }
  };
  
  const handleMoveOptionDown = (index) => {
    if (index === dropdownOptions.length - 1) return; // Can't move down if already at the bottom
    
    if (selectedElement.children && selectedElement.children.length > index + 1) {
      // Create a copy of the children array
      const updatedChildren = [...selectedElement.children];
      
      // Swap the children
      const tempChild = updatedChildren[index];
      updatedChildren[index] = updatedChildren[index + 1];
      updatedChildren[index + 1] = tempChild;
      
      // Create updated dropdown with new children order
      const updatedDropdown = {
        ...selectedElement,
        children: updatedChildren
      };
      
      // Also update the dropdownOptions array to match
      const updatedOptions = [...dropdownOptions];
      const tempOption = updatedOptions[index];
      updatedOptions[index] = updatedOptions[index + 1];
      updatedOptions[index + 1] = tempOption;
      setDropdownOptions(updatedOptions);
      
      // Update the selected element in both local and parent state
      setSelectedElement(updatedDropdown);
      updateElementText(selectedElement.id, updatedDropdown);
    }
  };
  
  // Updated function to update dropdown options - now a placeholder since we're updating the actual elements directly
  const updateDropdownOptions = (options) => {
    // Keep this function as a reference but the actual work is done elsewhere
    setDropdownOptions(options);
  };

  return (
    <div className="settings-panel">
      <h3>Setting Properties</h3>

      {/* Update the Visibility Controls */}
      <div className="setting-section">
        <label className="setting-label">Visibility</label>
        <div className="visibility-controls">
          <button
            className={`visibility-btn ${selectedElement.visibility !== 'hidden' ? 'active' : ''}`}
            onClick={() => handleVisibilityChange('visible')}
            title="Make element visible"
          >
            <FaEye />
            Visible
          </button>
          <button
            className={`visibility-btn ${selectedElement.visibility === 'hidden' ? 'active' : ''}`}
            onClick={() => handleVisibilityChange('hidden')}
            title="Hide element"
          >
            <FaEyeSlash />
            Hidden
          </button>
        </div>
      </div>
      
      {/* Add CartSettings section right after the visibility controls */}
      {selectedElement?.type === 'cart' && (
        <div className="setting-section">
          <CartSettings
            element={selectedElement}
            elements={elements}
            setElements={setElements}
            updateElementText={updateElementText}
            classStylesRegistry={classStylesRegistry}
            setClassStylesRegistry={setClassStylesRegistry}
            selectedDevice={selectedDevice}
            pages={pages}
            setPages={setPages}
          />
        </div>
      )}

      {/* Form Settings Section */}
      {isFormElement && (
        <div className="setting-section">
          <FormSetting 
            element={selectedElement}
            elements={elements}
            onUpdate={(updatedElement) => {
              // Update the element in the elements array
              const updatedElements = elements.map(el => 
                el.id === updatedElement.id ? updatedElement : el
              );
              // Update the selected element if it's the one being modified
              if (selectedElement.id === updatedElement.id) {
                setSelectedElement(updatedElement);
              }
              // Update the element text/properties
              updateElementText(updatedElement.id, updatedElement);
            }}
          />
        </div>
      )}

      {/* Text Editor Section - Enhanced UI */}
      {isTextEditable && (
        <div className="setting-section">
          <label className="setting-label">Text Content</label>
          
          {isTemplatePage || parentCmsElement ? (
            <div className="content-type-selector">
              {/* Content Type Toggle Buttons */}
              <div className="content-toggle-buttons">
                <button 
                  className={`content-toggle-btn ${selectedField === 'static' ? 'active' : ''}`}
                  onClick={() => {
                    updateElementText(selectedElement.id, {
                      ...selectedElement,
                      dynamicField: undefined,
                      text: text || ''
                    });
                    setSelectedField('static');
                  }}
                >
                  <FaFont className="toggle-icon" />
                  <span>Static</span>
                </button>
                <button 
                  className={`content-toggle-btn ${selectedField !== 'static' ? 'active' : ''}`}
                  onClick={() => {
                    // If no field is selected yet, select the first available field
                    if (selectedField === 'static') {
                      const availableFields = isTemplatePage 
                        ? Object.keys(selectedContentItem?.data || {})
                        : parentContentType?.fields?.map(f => f.name) || [];
                      
                      if (availableFields.length > 0) {
                        handleFieldSelect(availableFields[0]);
                        setSelectedField(availableFields[0]);
                      }
                    }
                  }}
                >
                  <FaBolt className="toggle-icon" />
                  <span>Dynamic</span>
                </button>
              </div>
              
              {/* Content Editor Based on Selection */}
              <div className="content-editor-container">
                {selectedField === 'static' ? (
                  <div className="static-content-editor">
                    <textarea
                      value={text}
                      onChange={handleTextChange}
                      className="text-editor"
                      placeholder={`Enter ${selectedElement.type} text...`}
                      rows={3}
                    />
                  </div>
                ) : (
                  <div className="dynamic-content-editor">
                    <div className="field-selection">
                      <label>CMS Field</label>
                      <select 
                        value={selectedField}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleFieldSelect(value);
                          setSelectedField(value);
                        }}
                        className="setting-select"
                      >
                        {isTemplatePage 
                          ? Object.keys(selectedContentItem?.data || {}).map(field => (
                              <option key={field} value={field}>{field}</option>
                            ))
                          : parentContentType?.fields?.map(field => (
                              <option key={field.name} value={field.name}>{field.label}</option>
                            ))
                        }
                      </select>
                    </div>
                    
                    <div className="dynamic-preview">
                      <label>Preview</label>
                      <div className="preview-value">
                        {selectedContentItem?.data?.[selectedField] || <span className="empty-value">(empty)</span>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Regular text editor for non-template pages
            <textarea
              value={text}
              onChange={handleTextChange}
              className="text-editor"
              placeholder={`Enter ${selectedElement.type} text...`}
              rows={3}
            />
          )}
        </div>
      )}

      {/* Page Link Section */}
      {isLinkable && (
        <div className="setting-section">
          <label className="setting-label">Link to Page</label>
          <select 
            value={selectedPage} 
            onChange={handlePageSelect}
            className="page-select"
          >
            <option value="">Select a page...</option>
            {pages.map(page => (
              <option key={page.id} value={page.isHome ? 'index' : page.slug || page.name}>
                {page.name} {page.isHome ? '(Home)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Image Settings Section - Enhanced UI */}
      {selectedElement.type === "image" && (
        <div className="setting-section">
          <label className="setting-label">Image</label>
          
          {isTemplatePage || parentCmsElement ? (
            <div className="content-type-selector">
              {/* Image Type Toggle Buttons */}
              <div className="content-toggle-buttons">
                <button 
                  className={`content-toggle-btn ${selectedImageField === 'static' ? 'active' : ''}`}
                  onClick={() => {
                    // Remove dynamic image field if static is selected
                    const updatedElement = {
                      ...selectedElement,
                      dynamicImageField: undefined
                    };
                    updateElementText(selectedElement.id, updatedElement);
                    setSelectedImageField('static');
                  }}
                >
                  <FaImage className="toggle-icon" />
                  <span>Static</span>
                </button>
                <button 
                  className={`content-toggle-btn ${selectedImageField !== 'static' ? 'active' : ''}`}
                  onClick={() => {
                    // If no field is selected yet, select the first available field
                    if (selectedImageField === 'static') {
                      const availableFields = isTemplatePage 
                        ? Object.keys(selectedContentItem?.data || {})
                        : parentContentType?.fields?.map(f => f.name) || [];
                      
                      if (availableFields.length > 0) {
                        handleImageFieldSelect(availableFields[0]);
                      }
                    }
                  }}
                >
                  <FaBolt className="toggle-icon" />
                  <span>Dynamic</span>
                </button>
              </div>
              
              {/* Image Editor Based on Selection */}
              <div className="content-editor-container">
                {selectedImageField === 'static' ? (
                  <div className="static-image-editor">
                    <ImageBox
                      image={selectedImage || {
                        url: "https://placehold.co/150x150",
                        name: "New Image",
                        dimensions: {
                          width: 150,
                          height: "auto",
                        },
                      }}
                      onReplace={() => setShowAssetsSidebar(true)}
                    />
                  </div>
                ) : (
                  <div className="dynamic-image-editor">
                    <div className="field-selection">
                      <label>CMS Field</label>
                      <select 
                        value={selectedImageField}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleImageFieldSelect(value);
                        }}
                        className="setting-select"
                      >
                        {isTemplatePage 
                          ? Object.keys(selectedContentItem?.data || {}).map(field => (
                              <option key={field} value={field}>{field}</option>
                            ))
                          : parentContentType?.fields?.map(field => (
                              <option key={field.name} value={field.name}>{field.label || field.name}</option>
                            ))
                        }
                      </select>
                    </div>
                    
                    <div className="dynamic-image-preview">
                      <label>Preview</label>
                      {selectedImage && selectedImage.url && selectedImage.url !== "https://placehold.co/150x150" ? (
                        <div className="preview-image-container">
                          <img 
                            src={selectedImage.url} 
                            alt={`Preview of ${selectedImageField}`} 
                            style={{maxWidth: '100%', maxHeight: '150px'}} 
                          />
                        </div>
                      ) : (
                        <div className="empty-image-container">
                          <div className="empty-image-placeholder">
                            <span className="placeholder-icon">📷</span>
                            <p>No image available in this field</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Regular image editor for non-template pages
            <ImageBox
              image={selectedImage || {
                url: "https://placehold.co/150x150",
                name: "New Image",
                dimensions: {
                  width: 150,
                  height: "auto",
                },
              }}
              onReplace={() => setShowAssetsSidebar(true)}
            />
          )}
        </div>
      )}

      {/* Add this section before the CMS Settings section */}
      {selectedElement?.type === 'slider' && (
        <SliderSettings
          element={selectedElement}
          elements={elements}
          setElements={setElements}
          setSelectedElement={setSelectedElement}
        />
      )}

      {/* CMS Settings Section */}
      {selectedElement?.type === 'cms-content' && (
        <div className="setting-section">
          <label className="setting-label">CMS Settings</label>
          
          {/* Content Type Selection */}
          <div className="form-group">
            <label>Content Type</label>
            <select 
              value={contentTypeId}
              onChange={handleContentTypeChange}
              className="setting-select"
            >
              <option value="">Select Content Type</option>
              {cmsContentTypes.map(type => (
                <option key={type._id} value={type._id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {/* Display Type */}
          <div className="form-group">
            <label>Display Type</label>
            <select 
              value={displayType}
              onChange={handleDisplayTypeChange}
              className="setting-select"
            >
              <option value="list">List</option>
              <option value="grid">Grid</option>
              <option value="cards">Cards</option>
            </select>
          </div>

          {/* Item Limit */}
          <div className="form-group">
            <label>Item Limit</label>
            <input 
              type="number"
              value={limit}
              onChange={handleLimitChange}
              min="1"
              max="100"
              className="setting-input"
            />
          </div>

          {/* Sort Options */}
          <div className="form-group">
            <label>Sort By</label>
            <select 
              value={sortBy}
              onChange={handleSortByChange}
              className="setting-select"
            >
              <option value="createdAt">Date Created</option>
              <option value="updatedAt">Date Updated</option>
              <option value="title">Title</option>
            </select>
          </div>

          <div className="form-group">
            <label>Sort Order</label>
            <select 
              value={sortOrder}
              onChange={handleSortOrderChange}
              className="setting-select"
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>

          {/* Content Preview */}
          {contentTypeId && (
            <div className="setting-group">
              <label>Content Preview</label>
              <div className="cms-preview">
                {cmsContent[contentTypeId] ? (
                  <p>{cmsContent[contentTypeId].length} items available</p>
                ) : (
                  <p>No content available</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab Settings Section */}
      {selectedElement?.type === 'div' && selectedElement?.children?.length === 2 && (
        <div className="setting-section">
          <TabSettings 
            element={selectedElement}
            elements={elements}
            onUpdate={(updatedElements) => {
              // Update elements in the main state
              setElements(updatedElements);
              // Update selected element if it was modified
              const updatedSelectedElement = updatedElements.find(el => el.id === selectedElement.id);
              if (updatedSelectedElement) {
                setSelectedElement(updatedSelectedElement);
              }
            }}
          />
        </div>
      )}

      {/* Map Settings Section */}
      {selectedElement.type === 'map' && (
        <div className="setting-section">
          <h4>Map Settings</h4>
          
          <div className="setting-group">
            <label className="setting-label">Location</label>
            <input
              type="text"
              value={mapLocation}
              onChange={handleMapLocationChange}
              className="setting-input"
              placeholder="Enter location (e.g., New York, NY)"
            />
          </div>
          
          <div className="setting-group">
            <label className="setting-label">Zoom Level (1-20)</label>
            <div className="range-with-value">
              <input
                type="range"
                min="1"
                max="20"
                value={mapZoom}
                onChange={handleMapZoomChange}
                className="setting-range"
              />
              <span className="range-value">{mapZoom}</span>
            </div>
          </div>
        </div>
      )}

      {/* Video Settings Section */}
      {selectedElement.type === 'video' && (
        <div className="setting-section">
          <h4>Video Settings</h4>
          
          <div className="setting-group">
            <label className="setting-label">Video Type</label>
            <select
              value={videoType}
              onChange={handleVideoTypeChange}
              className="setting-select"
            >
              <option value="youtube">YouTube</option>
              <option value="vimeo">Vimeo</option>
              <option value="custom">Custom URL</option>
            </select>
          </div>
          
          <div className="setting-group">
            <label className="setting-label">
              {videoType === 'youtube' ? 'YouTube Video ID or URL' : 
               videoType === 'vimeo' ? 'Vimeo Video ID or URL' : 
               'Video Embed URL'}
            </label>
            <input
              type="text"
              value={videoSrc}
              onChange={handleVideoSrcChange}
              className="setting-input"
              placeholder={
                videoType === 'youtube' ? 'e.g., dQw4w9WgXcQ or full YouTube URL' : 
                videoType === 'vimeo' ? 'e.g., 76979871 or full Vimeo URL' : 
                'Enter full video embed URL'
              }
            />
          </div>
          
          <div className="setting-group">
            <h5 className="subsetting-label">Playback Options</h5>
            
            <div className="checkbox-setting">
              <input
                type="checkbox"
                id="video-autoplay"
                checked={autoplay}
                onChange={handleToggleAutoplay}
              />
              <label htmlFor="video-autoplay">Autoplay</label>
            </div>
            
            <div className="checkbox-setting">
              <input
                type="checkbox"
                id="video-controls"
                checked={controls}
                onChange={handleToggleControls}
              />
              <label htmlFor="video-controls">Show Controls</label>
            </div>
            
            <div className="checkbox-setting">
              <input
                type="checkbox"
                id="video-loop"
                checked={loop}
                onChange={handleToggleLoop}
              />
              <label htmlFor="video-loop">Loop Video</label>
            </div>
            
            <div className="checkbox-setting">
              <input
                type="checkbox"
                id="video-mute"
                checked={mute}
                onChange={handleToggleMute}
              />
              <label htmlFor="video-mute">Mute Audio</label>
            </div>
          </div>
          
          <div className="setting-note">
            <small>Note: Autoplay usually requires muting the video due to browser policies.</small>
          </div>
        </div>
      )}

      {/* Embed Code Section */}
      {selectedElement.type === "embed" && (
        <div className="setting-group">
          <label className="setting-label">Embed Code</label>
          <p className="setting-hint">
            This code will be rendered directly on the published site. Be careful with the code you add as it runs on the client's browser.
          </p>
          <div className="embed-code-panel">
            <textarea
              className="embed-code-editor"
              value={embedCode}
              onChange={handleEmbedCodeChange}
              placeholder="Paste HTML or script code here..."
              rows={6}
            />
            <button 
              className="open-embed-editor-button"
              onClick={handleOpenEmbedCodeEditor}
            >
              <FaCode /> Open Full Editor
            </button>
          </div>
          {showEmbedCodeEditor && (
            <EmbedCodeEditor
              value={embedCode}
              onChange={setEmbedCode}
              onClose={handleCloseEmbedCodeEditor}
              onSave={handleSaveEmbedCode}
            />
          )}
        </div>
      )}

      {/* Dropdown Settings Section */}
      {selectedElement.type === "dropdown" && (
        <div className="setting-section">
          <h4>Dropdown Options</h4>
          
          {/* List of current options */}
          <div className="dropdown-options-list">
            {dropdownOptions.length === 0 ? (
              <p className="empty-state">No options added yet. Add options below.</p>
            ) : (
              <ul className="options-list">
                {dropdownOptions.map((option, index) => (
                  <li key={index} className="option-item">
                    <div className="option-details">
                      <span className="option-label">{option.label}</span>
                      <span className="option-value">Value: {option.value}</span>
                    </div>
                    <div className="option-actions">
                      <button 
                        className="move-option-btn"
                        onClick={() => handleMoveOptionUp(index)}
                        disabled={index === 0}
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button 
                        className="move-option-btn"
                        onClick={() => handleMoveOptionDown(index)}
                        disabled={index === dropdownOptions.length - 1}
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button 
                        className="remove-option-btn"
                        onClick={() => handleRemoveDropdownOption(index)}
                        title="Remove option"
                      >
                        ✕
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          {/* Add new option form */}
          <div className="add-option-form">
            <h5>Add Option</h5>
            <div className="option-input-group">
              <div className="form-group">
                <label>Display Text</label>
                <input
                  type="text"
                  value={newOptionLabel}
                  onChange={(e) => setNewOptionLabel(e.target.value)}
                  placeholder="Option label"
                  className="setting-input"
                />
              </div>
              <div className="form-group">
                <label>Value (optional)</label>
                <input
                  type="text"
                  value={newOptionValue}
                  onChange={(e) => setNewOptionValue(e.target.value)}
                  placeholder="option-value"
                  className="setting-input"
                />
                <small className="input-hint">If left empty, a value will be generated from the label</small>
              </div>
            </div>
            <button 
              className="add-option-button"
              onClick={handleAddDropdownOption}
              disabled={!newOptionLabel.trim()}
            >
              Add Option
            </button>
          </div>
        </div>
      )}

      {/* Navbar Settings Section */}
      {selectedElement.type === 'nav' && selectedElement.attributes?.['data-section-type'] === 'navbar' && (
        <div className="setting-section">
          <NavbarSettings
            element={selectedElement}
            elements={elements}
            setElements={setElements}
            updateElementText={updateElementText}
            pages={pages}
            selectedDevice={selectedDevice}
            classStylesRegistry={classStylesRegistry}
            setClassStylesRegistry={setClassStylesRegistry}
            assets={assets}
          />
        </div>
      )}

      {/* Rich Text Editor Section */}
      {selectedElement.type === "rich-text" && (
        <div className="setting-section">
          <label className="setting-label">Rich Text Content</label>
          
          {isTemplatePage || parentCmsElement ? (
            <div className="content-type-selector">
              {/* Content Type Toggle Buttons */}
              <div className="content-toggle-buttons">
                <button 
                  className={`content-toggle-btn ${selectedField === 'static' ? 'active' : ''}`}
                  onClick={() => {
                    updateElementText(selectedElement.id, {
                      ...selectedElement,
                      dynamicField: undefined,
                      text: text || ''
                    });
                    setSelectedField('static');
                  }}
                >
                  <FaFont className="toggle-icon" />
                  <span>Static</span>
                </button>
                <button 
                  className={`content-toggle-btn ${selectedField !== 'static' ? 'active' : ''}`}
                  onClick={() => {
                    // If no field is selected yet, select the first available field
                    if (selectedField === 'static') {
                      // Filter to only get rich text fields
                      const availableFields = isTemplatePage 
                        ? Object.keys(selectedContentItem?.data || {}).filter(field => {
                            const fieldDef = selectedContentItem?.contentType?.fields?.find(f => f.name === field);
                            return fieldDef?.type === 'richText';
                          })
                        : parentContentType?.fields?.filter(f => f.type === 'richText').map(f => f.name) || [];
                      
                      if (availableFields.length > 0) {
                        handleFieldSelect(availableFields[0]);
                        setSelectedField(availableFields[0]);
                      }
                    }
                  }}
                >
                  <FaBolt className="toggle-icon" />
                  <span>Dynamic</span>
                </button>
              </div>
              
              {/* Content Editor Based on Selection */}
              <div className="content-editor-container">
                {selectedField === 'static' ? (
                  <div className="static-content-editor">
                    {showRichTextEditor && (
                      <RichTextEditor
                        value={text}
                        onChange={handleRichTextChange}
                        onBlur={() => setShowRichTextEditor(false)}
                        autoFocus={true}
                        renderInMainContainer={true}
                      />
                    )}
                    <div className="rich-text-preview-container">
                      <div 
                        className="rich-text-preview"
                        dangerouslySetInnerHTML={{ __html: text || '<p>No content</p>' }}
                      />
                      <button 
                        className="edit-rich-text-btn" 
                        onClick={() => setShowRichTextEditor(true)}
                      >
                        <FaEdit className="edit-icon" />
                        Edit Rich Text
                      </button>
                      <div className="setting-help">
                        <p>Click the button above to open the rich text editor.</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="dynamic-content-editor">
                    <div className="field-selection">
                      <label>CMS Rich Text Field</label>
                      <select 
                        value={selectedField}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleFieldSelect(value);
                          setSelectedField(value);
                        }}
                        className="setting-select"
                      >
                        {isTemplatePage 
                          ? Object.keys(selectedContentItem?.data || {})
                              .filter(field => {
                                // Filter to only show rich text fields
                                const fieldDef = selectedContentItem?.contentType?.fields?.find(f => f.name === field);
                                return fieldDef?.type === 'richText';
                              })
                              .map(field => (
                                <option key={field} value={field}>{field}</option>
                              ))
                          : parentContentType?.fields
                              ?.filter(field => field.type === 'richText')
                              .map(field => (
                                <option key={field.name} value={field.name}>{field.label}</option>
                              ))
                        }
                      </select>
                    </div>
                    
                    <div className="dynamic-preview">
                      <label>Rich Text Preview</label>
                      <div 
                        className="rich-text-preview"
                        dangerouslySetInnerHTML={{ __html: selectedContentItem?.data?.[selectedField] || '<p><em>(No content)</em></p>' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Regular text editor for non-template pages
            <div className="rich-text-preview-container">
              {showRichTextEditor && (
                <RichTextEditor
                  value={text}
                  onChange={handleRichTextChange}
                  onBlur={() => setShowRichTextEditor(false)}
                  autoFocus={true}
                  renderInMainContainer={true}
                />
              )}
              <div>
                <div 
                  className="rich-text-preview"
                  dangerouslySetInnerHTML={{ __html: text || '<p>No content</p>' }}
                />
                <button 
                  className="edit-rich-text-btn" 
                  onClick={() => setShowRichTextEditor(true)}
                >
                  <FaEdit className="edit-icon" />
                  Edit Rich Text
                </button>
                <div className="setting-help">
                  <p>Click the button above to open the rich text editor.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Setting;
